import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, Container, Chip, Stack, Paper, Skeleton, Divider, Grid, Link } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DiamondIcon from '@mui/icons-material/Diamond';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// TOP 7 CRYPTO COINS
const TOP_COINS = [
  { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin', color: '#f7931a' },
  { id: 'ethereum', symbol: 'ETH', name: 'Ethereum', color: '#627eea' },
  { id: 'tether', symbol: 'USDT', name: 'Tether', color: '#00d1ff' },
  { id: 'ripple', symbol: 'XRP', name: 'XRP', color: '#00aae4' },
  { id: 'usd-coin', symbol: 'USDC', name: 'USDC', color: '#00ff88' },
  { id: 'solana', symbol: 'SOL', name: 'Solana', color: '#9945ff' },
  { id: 'cardano', symbol: 'ADA', name: 'Cardano', color: '#0033ad' },
];

function Hero() {
  const [coinData, setCoinData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const ids = TOP_COINS.map(c => c.id).join(',');
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h`
        );
        
        if (!response.ok) throw new Error('API failed');

        const data = await response.json();
        if (!data || data.length === 0) throw new Error('Empty data');

        setCoinData(
          data.map((coin) => ({
            symbol: coin.symbol?.toUpperCase() || 'N/A',
            name: coin.name || 'Unknown',
            price: coin.current_price || 0,
            change24h: coin.price_change_percentage_24h || 0,
            marketCap: coin.market_cap || 0,
            volume: coin.total_volume || 0,
            supply: coin.circulating_supply || 0,
            color: TOP_COINS.find(c => c.id === coin.id)?.color || '#ffffff'
          }))
        );
      } catch (e) {
        setCoinData([
          { symbol: 'BTC', name: 'Bitcoin', price: 79350, change24h: 2.03, marketCap: 1590000000000, volume: 56710000000, color: '#f7931a' },
          { symbol: 'ETH', name: 'Ethereum', price: 2477.94, change24h: 0.23, marketCap: 299040000000, volume: 20220000000, color: '#627eea' },
          { symbol: 'USDT', name: 'Tether', price: 0.9998, change24h: 0.01, marketCap: 183190000000, volume: 91530000000, color: '#00d1ff' },
          { symbol: 'XRP', name: 'XRP', price: 1.48, change24h: -0.27, marketCap: 92870000000, volume: 5560000000, color: '#00aae4' },
          { symbol: 'USDC', name: 'USDC', price: 0.9999, change24h: 0.01, marketCap: 73590000000, volume: 24010000000, color: '#00ff88' },
          { symbol: 'SOL', name: 'Solana', price: 99.57, change24h: 5.01, marketCap: 58090000000, volume: 7420000000, color: '#9945ff' },
          { symbol: 'ADA', name: 'Cardano', price: 0.2195, change24h: -0.53, marketCap: 8230000000, volume: 606930000, color: '#0033ad' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatLargeNum = (num) => {
    if (!num || isNaN(num)) return '$0.00';
    if (num >= 1_000_000_000_000) return `$${(num / 1_000_000_000_000).toFixed(2)}T`;
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    return `$${num.toFixed(0)}`;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: 'auto',
        minHeight: { xs: '550px', md: '650px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundImage: 'url(/images/hero.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pb: { xs: '60px', md: '80px' },
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'linear-gradient(to right, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.85) 50%, rgba(0, 0, 0, 0.70) 100%)', 
        zIndex: 1 
      }} />
      
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundImage: 'url(/images/hero.png)', 
        backgroundSize: 'cover', 
        backgroundPosition: 'center', 
        animation: 'slowZoom 30s ease-in-out infinite alternate', 
        '@keyframes slowZoom': { '0%': { transform: 'scale(1)' }, '100%': { transform: 'scale(1.1)' } }, 
        zIndex: 0 
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10, pt: { xs: '100px', md: '120px' } }}>
        
        <Grid container spacing={12} alignItems="center">
          
          {/* LEFT: Live Market Panel */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                p: { xs: 2.5, md: 3.5 },
                background: 'linear-gradient(135deg, rgba(17, 17, 17, 0.95) 0%, rgba(5, 5, 5, 0.95) 100%)',
                border: '1px solid rgba(165, 164, 164, 0.2)',
                borderRadius: '12px',
                boxShadow: '0 30px 80px rgba(0, 0, 0, 0.8), 0 0 40px rgba(165, 164, 164, 0.05)',
                backdropFilter: 'blur(16px)',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '40%',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.03), transparent)',
                  pointerEvents: 'none',
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5, position: 'relative', zIndex: 1 }}>
                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontFamily: "'Playfair Display', serif", fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 0.75, letterSpacing: '0.5px' }}>
                  <TrendingUpIcon sx={{ color: '#d4af37', fontSize: '20px' }} /> Market Overview
                </Typography>
                <Chip
                  size="small"
                  label="LIVE"
                  sx={{
                    bgcolor: 'rgba(212, 175, 55, 0.15)',
                    color: '#d4af37', 
                    fontWeight: 700,
                    fontSize: '0.5rem',
                    borderRadius: '20px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    height: '18px',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(212, 175, 55, 0.5)' },
                      '70%': { boxShadow: '0 0 0 8px rgba(212, 175, 55, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(212, 175, 55, 0)' },
                    },
                  }}
                />
              </Box>

              {loading ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                    <Skeleton key={item} variant="rounded" height={30} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                  {coinData.map((coin) => (
                    <Box key={coin.symbol} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.25,
                      backgroundColor: 'rgba(255, 255, 255, 0.01)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      transition: 'all 0.3s ease',
                      '&:hover': { borderColor: 'rgba(212, 175, 55, 0.2)', backgroundColor: 'rgba(212, 175, 55, 0.03)' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: '90px' }}>
                        <Box sx={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DiamondIcon sx={{ color: coin.color, fontSize: '12px' }} /> 
                        </Box>
                        <Box>
                          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
                            {coin.symbol}
                          </Typography>
                          <Typography sx={{ color: '#a1a1a1', fontSize: '0.45rem' }}>
                            {coin.name.length > 8 ? coin.name.substring(0, 6) + '..' : coin.name}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.75rem' }}>
                          ${Number(coin.price).toFixed(coin.price < 1 ? 4 : 2)}
                        </Typography>
                        <Typography sx={{ color: Number(coin.change24h) >= 0 ? coin.color : '#ff5f56', fontSize: '0.6rem', fontWeight: 600 }}>
                          {Number(coin.change24h) >= 0 ? '+' : ''}{Number(coin.change24h).toFixed(2)}%
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: '#a1a1a1', fontSize: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Mkt Cap
                          </Typography>
                          <Typography sx={{ color: '#ffffff', fontSize: '0.6rem', fontWeight: 600 }}>
                            {formatLargeNum(coin.marketCap)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: '#a1a1a1', fontSize: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Vol
                          </Typography>
                          <Typography sx={{ color: '#ffffff', fontSize: '0.6rem', fontWeight: 600 }}>
                            {formatLargeNum(coin.volume)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}

              <Divider sx={{ borderColor: 'rgba(165, 164, 164, 0.08)', my: 1.5, position: 'relative', zIndex: 1 }} />
              
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Link
                  href="https://www.coingecko.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#666666',
                    fontSize: '0.5rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#a1a1a1',
                      '& .MuiSvgIcon-root': {
                        opacity: 1,
                      }
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '10px',
                      opacity: 0.5,
                      transition: 'opacity 0.3s ease',
                    }
                  }}
                >
                  Source: CoinGecko · Refreshes every 60s
                  <OpenInNewIcon />
                </Link>
                <Typography variant="caption" sx={{ color: '#555555', fontSize: '0.5rem' }}>
                  *Top 7 by Market Cap
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT: 3D Logo with Tagline Only - Lowered */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
              pl: { md: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: { xs: 'auto', md: '100%' },
              pt: { xs: 2, md: 6 },
            }}>
              {/* 3D Logo - Lowered with margin top */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                mb: 3,
                mt: { xs: 0, md: 4 },
              }}>
                <img 
                  src="/images/usdd.png" 
                  alt="Diamond Dollar 3D Logo" 
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    height: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 0 60px rgba(212, 175, 55, 0.35))',
                    animation: 'float 6s ease-in-out infinite',
                  }}
                />
                <style>
                  {`
                    @keyframes float {
                      0%, 100% { transform: translateY(0px); }
                      50% { transform: translateY(-15px); }
                    }
                  `}
                </style>
              </Box>
              
              <Typography 
                variant="h3" 
                sx={{ 
                  fontFamily: "'Playfair Display', serif",
                  color: '#d4af37',
                  fontWeight: 600,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  letterSpacing: '4px',
                  textAlign: 'center',
                  textShadow: '0 0 40px rgba(212, 175, 55, 0.15)',
                }}
              >
                The New Standard of Value
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5, justifyContent: 'center', mt: 1 }}>
                {/* Empty stack - keeping only what you have */}
              </Stack>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}

export default Hero;